import { FC, PropsWithChildren as PWC, useCallback, useState } from 'react'
import { usePathname, useRouter } from 'next/navigation'
import { gql } from '@apollo/client'
import Link from 'next/link'

import Icon from '~/src/components/generic/PhosphorIcons/Icon'

import MobileSettingsMenu from '~/src/components/shared/NavBar/components/MobileSettingsMenu'
import { ActionEnum } from '~/src/components/shared/NavBar/types'
import UserProfileIcon from '~/src/components/shared/UserProfileIcon'

import { isRadiusUser } from '~/helpers/auth'

import { useAuthenticatedUser } from '~/hooks/useAuthentication'

import { tw } from '~/lib/utils'
import ReportIssue from '~/src/components/ReportIssue'
import { MixpanelEvent, resetMixpanel, track } from '~/clients/mixpanelClient'
import { useTabTracking } from '~/app/(authenticated)/useTabTracking'

//TODO: move it to separate file
export const LOGOUT = gql`
  mutation Logout {
    logout {
      id
    }
  }
`
type Props = {
  onOpenSettings?: () => void
  title?: string
  className?: string
}
const MobileNavBar: FC<PWC<Props>> = ({
  title,
  children,
  className,
  onOpenSettings,
}) => {
  const [openReportIssue, setOpenReportIssue] = useState(false)
  const [user, logout] = useAuthenticatedUser()
  const pathName = usePathname()

  useTabTracking('Mobile Dispatch')

  const shouldShowUserConfig = isRadiusUser(user)

  const router = useRouter()

  const handleReportIssueClose = useCallback(() => {
    setOpenReportIssue(!openReportIssue)
  }, [openReportIssue])

  const handleOptionSelect = useCallback(
    (action: ActionEnum) => {
      switch (action) {
        case ActionEnum.ReportIssue:
          setOpenReportIssue(!openReportIssue)
          break
        case ActionEnum.Settings:
          onOpenSettings?.()
          break
        case ActionEnum.Logout:
          logout({
            onCompleted: (data) => {
              track(MixpanelEvent.UserLogout, { userId: data.logout.id })
              resetMixpanel()
              router.push('/login')
            },
          })
          break
        default:
          break
      }
    },
    [logout, onOpenSettings, openReportIssue, router]
  )

  const hideBackButton = pathName === '/home'

  if (!hideBackButton) return null

  // TODO: Update ReportIssue to use new shared Support Form.  Not needed for MVP
  // Related ticket: https://app.asana.com/0/1208364038231390/1208741775595921
  return (
    <header
      className={tw(
        'sticky top-0 z-10 flex h-[60px] w-full items-center justify-between border-b border-solid border-gray-200 bg-white px-4',
        className
      )}
      data-testid="navbar"
    >
      <div className="flex items-center">
        {!hideBackButton && (
          <Link
            className="-ml-1 rounded-lg p-1 transition-colors hover:bg-blue-50"
            href="/home"
          >
            <Icon name="ArrowLeftIcon" size="20" />
          </Link>
        )}
        {title && (
          <div className="ml-1 text-body-m font-bold text-default">{title}</div>
        )}
        {children}
      </div>

      <div className="flex flex-row">
        {shouldShowUserConfig ? (
          <div className="flex gap-4">
            <div className="flex items-center rounded-full hover:bg-gray-300">
              <UserProfileIcon profilePictureUrl={user?.oauthProfilePictureUrl} />
              <MobileSettingsMenu onOptionSelect={handleOptionSelect} />
            </div>
          </div>
        ) : (
          <UserProfileIcon profilePictureUrl={user?.oauthProfilePictureUrl} />
        )}
      </div>
      {openReportIssue && (
        <ReportIssue
          isOpen={openReportIssue}
          isRadiusUser={isRadiusUser(user)}
          isV2={false}
          onCloseHandler={handleReportIssueClose}
        />
      )}
    </header>
  )
}

export default MobileNavBar
